import Routes from '../../config/Routes/Routes';
import { useTranslation } from 'react-i18next';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faMedal,
  faShoppingCart,
  faSignalStream,
  faHeadset,
  faHome,
  faTrophy,
  faJoystick,
  faCloud,
} from 'HiveClient/components/Icons/Icons';
import { faDiscord } from '@fortawesome/free-brands-svg-icons';
import GlobalClientConfig from 'HiveClient/config/GlobalClientConfig/GlobalClientConfig';
import { useFeatureFlagsContext } from '../../config/FeatureFlags/FeatureFlagsContext';
import { FeaturesEnum } from '../../config/FeatureFlags/FeaturesEnum';
import {
  ClientNavLinks,
  CustomNavLinksOrder,
} from 'HiveClient/config/ClientNavLinks';
import { faBlog } from '@fortawesome/pro-regular-svg-icons';
import { NavLinksSemantics } from './NavLinksSemanticsEnum';

const navLinksSorter = (a: INavLink, b: INavLink) =>
  (CustomNavLinksOrder[a.semantic] ?? Number.MAX_SAFE_INTEGER) -
  (CustomNavLinksOrder[b.semantic] ?? Number.MAX_SAFE_INTEGER);

export interface INavLink<ClientNavLinksSemantics = NavLinksSemantics> {
  url: string;
  title: string;
  icon: IconDefinition;
  semantic: NavLinksSemantics | ClientNavLinksSemantics;
  external?: boolean;
  hideOnMobile?: boolean;
  hideOnHorizontal?: boolean;
  hideOnVertical?: boolean;
}

const useGetNavLinks = () => {
  const { t } = useTranslation();

  const { checkFeatureFlags } = useFeatureFlagsContext();

  let navLinks: INavLink[] = [
    {
      url: Routes.home,
      title: t('dashboard.nav-links.home'),
      icon: faHome,
      hideOnMobile: true,
      hideOnHorizontal: true,
      semantic: NavLinksSemantics.Home,
    },
    {
      url: t('dashboard.nav-links.support-url'),
      external: true,
      title: t('dashboard.nav-links.support'),
      hideOnMobile: true,
      icon: faHeadset,
      semantic: NavLinksSemantics.Support,
    },
  ];

  if (checkFeatureFlags(FeaturesEnum.Arcade)) {
    navLinks.push({
      url: Routes.arcade,
      title: t('dashboard.nav-links.arcade'),
      icon: faJoystick,
      semantic: NavLinksSemantics.Arcade,
    });
  }

  if (!GlobalClientConfig.navigation.hideGameServers) {
    navLinks.push({
      url: Routes.serverList,
      title: t('dashboard.nav-links.game-servers'),
      hideOnMobile: true,
      icon: faCloud,
      semantic: NavLinksSemantics.Servers,
    });
  }

  if (GlobalClientConfig.navigation.discordURL) {
    navLinks.push({
      url: GlobalClientConfig.navigation.discordURL,
      external: true,
      title: t('dashboard.nav-links.discord'),
      hideOnMobile: true,
      icon: faDiscord,
      semantic: NavLinksSemantics.Discord,
    });
  }

  if (checkFeatureFlags(FeaturesEnum.Events)) {
    if (!GlobalClientConfig.navigation.eventCategories) {
      navLinks.push({
        url: Routes.events,
        title: t('dashboard.nav-links.events'),
        icon: faTrophy,
        semantic: NavLinksSemantics.Events,
      });
    } else {
      navLinks.push(
        {
          url: Routes.tournaments,
          title: t('dashboard.nav-links.tournaments'),
          icon: faTrophy,
          semantic: NavLinksSemantics.Tournaments,
        },
        {
          url: Routes.challenges,
          title: t('dashboard.nav-links.challenges', 'Challenges'),
          icon: faMedal,
          semantic: NavLinksSemantics.Challenges,
        }
      );
    }
  }

  if (checkFeatureFlags(FeaturesEnum.Streams)) {
    navLinks.push({
      url: Routes.watchStreams,
      title: t('dashboard.nav-links.watch'),
      icon: faSignalStream,
      semantic: NavLinksSemantics.Watch,
    });
  }

  if (checkFeatureFlags(FeaturesEnum.Store)) {
    navLinks.push({
      url: Routes.store,
      title: t('dashboard.nav-links.store'),
      icon: faShoppingCart,
      semantic: NavLinksSemantics.Store,
    });
  }

  if (ClientNavLinks.length > 0) {
    ClientNavLinks.forEach((link) => {
      navLinks.push(link);
    });
  }

  if (checkFeatureFlags(FeaturesEnum.Blog)) {
    navLinks.push({
      url: Routes.blog,
      title: t('dashboard.nav-links.blog'),
      icon: faBlog,
      semantic: NavLinksSemantics.Blog,
    });
  }

  return navLinks.sort(navLinksSorter);
};

export default useGetNavLinks;
